import { lazy, Suspense } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import LoadingSpinner from "../components/LoadingSpinner";
import PrivateRoute from "../components/PrivateRoute";
import DeveloperRoute from "../components/DeveloperRoute";
import AdminRoute from "../components/AdminRoute";
import { UserRole } from "../types/church";

// Componentes carregados imediatamente (páginas iniciais/leves)
import Login from "../components/Login";
import Dashboard from "../components/Dashboard";
import AreaVisitante from "../components/AreaVisitante";
import NotFound from "../components/NotFound";

// Lazy loading para componentes pesados
const GerenciarMembros = lazy(() => import("../components/GerenciarMembros"));
const GerenciamentoFinanceiro = lazy(() => import("../components/GerenciamentoFinanceiro"));
const Perfil = lazy(() => import("../components/Perfil"));
const MinhasContribuicoes = lazy(() => import("../components/MinhasContribuicoes"));
const GerenciarRoles = lazy(() => import("../components/GerenciarRoles"));
const GerenciarDevocionais = lazy(() => import("../components/GerenciarDevocionais"));
const GerenciarImagensPublicas = lazy(() => import("../components/GerenciarImagensPublicas"));
const RelatorioAberto = lazy(() => import("../components/RelatorioAberto"));
const GerenciarUsuarios = lazy(() => import("../components/GerenciarUsuarios"));
const Eventos = lazy(() => import("../components/Eventos"));
const GerenciarEventos = lazy(() => import("../components/GerenciarEventos"));
const GerenciarEscalas = lazy(() => import("../components/GerenciarEscalas"));
const MinhasEscalas = lazy(() => import("../components/MinhasEscalas"));
const GerenciarArquivos = lazy(() => import("../components/GerenciarArquivos"));
const GerenciarPatrimonio = lazy(() => import("../components/GerenciarPatrimonio"));
const GerenciarConvites = lazy(() => import("../components/GerenciarConvites"));
const ListaIrregularidades = lazy(() => import("../components/ListaIrregularidades"));
const ReportarIrregularidade = lazy(() => import("../components/ReportarIrregularidade"));
const NotificationTestPanel = lazy(() => import("../components/NotificationTestPanel"));
const SendNotificationPanel = lazy(() => import("../components/SendNotificationPanel"));
const LeituraBiblica = lazy(() => import("../components/LeituraBiblica"));

// Wrapper com Suspense para componentes lazy
const LazyComponent = ({ children }) => (
  <Suspense fallback={<LoadingSpinner size="md" message="Carregando página..." fullPage />}>
    {children}
  </Suspense>
);

const AppRoutes = () => {
  return (
    <Routes>
      {/* Rotas públicas (carregadas imediatamente) */}
      <Route path="/login" element={<Login />} />
      <Route path="/visitante" element={<AreaVisitante />} />
      <Route path="/" element={<Navigate to="/visitante" />} />

      {/* Dashboard (carregado imediatamente após login) */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      {/* Rotas com Lazy Loading */}
      <Route
        path="/membros"
        element={
          <AdminRoute requiredRole="SEGUNDO_SECRETARIO">
            <LazyComponent>
              <GerenciarMembros />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/financeiro"
        element={
          <AdminRoute requiredRole="SEGUNDO_TESOUREIRO">
            <LazyComponent>
              <GerenciamentoFinanceiro />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/perfil"
        element={
          <PrivateRoute>
            <LazyComponent>
              <Perfil />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/minhas-contribuicoes"
        element={
          <PrivateRoute>
            <LazyComponent>
              <MinhasContribuicoes />
            </LazyComponent>
          </PrivateRoute>
        }
      />
<Route
        path="/gerenciar-roles"
        element={
          <AdminRoute requiredRole="PASTOR">
            <LazyComponent>
              <GerenciarRoles />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/test-notifications"
        element={
          <DeveloperRoute>
            <LazyComponent>
              <NotificationTestPanel />
            </LazyComponent>
          </DeveloperRoute>
        }
      />
      <Route
        path="/send-notifications"
        element={
          <AdminRoute requiredRole="PASTOR">
            <LazyComponent>
              <SendNotificationPanel />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/gerenciar-devocionais"
        element={
          <AdminRoute requiredRole="PRESBITERO">
            <LazyComponent>
              <GerenciarDevocionais />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/relatorio-aberto"
        element={
          <PrivateRoute>
            <LazyComponent>
              <RelatorioAberto />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/gerenciar-imagens-publicas"
        element={
          <AdminRoute requiredRole={UserRole.SECRETARIO}>
            <LazyComponent>
              <GerenciarImagensPublicas />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/gerenciar-usuarios"
        element={
          <DeveloperRoute>
            <LazyComponent>
              <GerenciarUsuarios />
            </LazyComponent>
          </DeveloperRoute>
        }
      />
      <Route
        path="/eventos"
        element={
          <PrivateRoute>
            <LazyComponent>
              <Eventos />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/gerenciar-eventos"
        element={
          <AdminRoute requiredRole="SEGUNDO_SECRETARIO">
            <LazyComponent>
              <GerenciarEventos />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/gerenciar-convites"
        element={
          <AdminRoute requiredRole="SEGUNDO_SECRETARIO">
            <LazyComponent>
              <GerenciarConvites />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/gerenciar-arquivos"
        element={
          <AdminRoute allowedRoles={["SECRETARIO", "SEGUNDO_SECRETARIO", "PASTOR", "DESENVOLVEDOR"]}>
            <LazyComponent>
              <GerenciarArquivos />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/gerenciar-escalas"
        element={
          <AdminRoute requiredRole="DIACONO">
            <LazyComponent>
              <GerenciarEscalas />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/minhas-escalas"
        element={
          <PrivateRoute>
            <LazyComponent>
              <MinhasEscalas />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/patrimonio"
        element={
          <AdminRoute requiredRole="DIRETOR_PATRIMONIO">
            <LazyComponent>
              <GerenciarPatrimonio />
            </LazyComponent>
          </AdminRoute>
        }
      />
      <Route
        path="/irregularidades"
        element={
          <PrivateRoute>
            <LazyComponent>
              <ListaIrregularidades />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/biblia"
        element={
          <PrivateRoute>
            <LazyComponent>
              <LeituraBiblica />
            </LazyComponent>
          </PrivateRoute>
        }
      />
      <Route
        path="/reportar-irregularidade"
        element={
          <PrivateRoute>
            <LazyComponent>
              <ReportarIrregularidade />
            </LazyComponent>
          </PrivateRoute>
        }
      />

      {/* Rota 404 - Deve ser a última rota */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
